import { Link } from "react-router-dom";
import { ArrowUpRight, BookOpen, Briefcase, Cpu, Heart } from "lucide-react";
import PageHero from "../components/sections/PageHero";

const arms = [
  { icon: BookOpen, title: "Capability Building", lead: "Outcome-driven learning experiences that prepare individuals for real-world performance.", points: ["Cohort-based deep-skill programs", "Live industry briefs as assessments", "Mentorship from industry teams, not lecturers", "16-week deploy-ready outcomes"] },
  { icon: Briefcase, title: "Talent Deployment", lead: "Bridging trained talent and industry demand through structured deployment models.", points: ["Recruitment Services", "Industry Hiring Partnerships", "Pre-deployment readiness audits", "Post-placement performance tracking"] },
  { icon: Cpu, title: "Deployedd Digital", lead: "Technology-enabled services that support hiring, training and workforce readiness at scale.", points: ["Skills assessment platform", "Cohort management SaaS", "Hiring-ops automation", "Workforce analytics dashboards"] },
  { icon: Heart, title: "Centre for Sustainability & EQ", lead: "Emotional intelligence and sustainability woven into workforce development.", points: ["EQ as core curriculum, not add-on", "Sustainability literacy modules", "Resilience labs for leaders", "Conscious development frameworks"] },
];

const Services = () => (
  <div data-testid="services-page">
    <PageHero
      kicker="/ Services — The Deployedd Ecosystem"
      title="An integrated model that"
      accent="transforms talent."
      breadcrumb={["The Deployedd Ecosystem"]}
      lead="Four operating arms, one architecture. Together they take an individual from foundation, through capability, into ownership of an outcome."
    />

    <section className="container-wide py-24 md:py-32">
      <div className="grid grid-cols-1 lg:grid-cols-2 border-t border-l border-ink">
        {arms.map((arm, i) => {
          const Icon = arm.icon;
          return (
            <div
              key={arm.title}
              data-testid={`service-arm-${i}`}
              className="border-r border-b border-ink p-10 md:p-14 group hover:bg-[#F3F3F1] transition-colors"
            >
              <div className="flex items-start justify-between mb-10">
                <p className="font-mono-cust text-[11px] uppercase tracking-[0.3em] text-[#9F381A]">
                  / 0{i + 1}
                </p>
                <Icon className="w-6 h-6 text-ink-muted group-hover:text-[#9F381A] transition-colors" />
              </div>
              <h2 className="font-heading text-3xl md:text-4xl tracking-tight mb-6">{arm.title}</h2>
              <p className="text-lg text-ink-muted mb-8 leading-relaxed">{arm.lead}</p>
              <ul className="space-y-3 border-t border-ink pt-6">
                {arm.points.map((pt) => (
                  <li key={pt} className="flex items-start gap-3 text-base">
                    <span className="font-mono-cust text-xs text-[#9F381A] mt-1.5">→</span>
                    <span>{pt}</span>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    </section>

    {/* Flagship K-I-A */}
    <section data-testid="services-kia" className="bg-ink text-white py-24 md:py-32">
      <div className="container-wide">
        <p className="font-mono-cust text-[11px] uppercase tracking-[0.3em] text-white/50 mb-6">/ Flagship Model</p>
        <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[0.95] mb-16 max-w-3xl">
          Knowledge → Innovation → <em className="font-editorial italic text-[#E68A6E]">Acceleration.</em>
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 border-t border-l border-white/15">
          {[
            { l: "K", t: "Knowledge", d: "We build strong foundations through structured learning, equipping individuals with the skills required to perform in real-world environments." },
            { l: "I", t: "Innovation", d: "We transform learning into capability through industry projects, startup cohorts and guided mentorship." },
            { l: "A", t: "Acceleration", d: "We convert capability into opportunity — placements, deployments, and CFT-led ventures with real investment-backed execution." },
          ].map((s) => (
            <div key={s.l} className="border-r border-b border-white/15 p-10 md:p-14 group">
              <span className="font-heading text-[14vw] md:text-[7vw] leading-none tracking-tighter text-[#E68A6E]/30 group-hover:text-[#E68A6E] transition-colors block mb-10">
                {s.l}
              </span>
              <h3 className="font-heading text-2xl md:text-3xl tracking-tight mb-4">{s.t}</h3>
              <p className="text-white/65 leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="container-wide py-24 md:py-32">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
        <div className="lg:col-span-8">
          <h2 className="font-heading text-4xl md:text-5xl tracking-tighter leading-[0.95]">
            Build a cohort with us.<br />
            <em className="font-editorial italic text-[#9F381A]">Deploy outcomes.</em>
          </h2>
        </div>
        <div className="lg:col-span-4">
          <Link to="/contact" data-testid="services-cta" className="inline-flex items-center justify-between w-full md:w-auto gap-3 bg-ink text-white px-6 py-4 text-[11px] uppercase tracking-[0.25em] hover:bg-[#9F381A] transition-colors">
            Start a conversation <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  </div>
);

export default Services;
